import { useCallback } from "react";

import isEmpty from "lodash/isEmpty";
import { useWindowDimensions } from "react-native";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import Intercom, { Visibility } from "@/crossPlatform/Intercom";
import useAuth from "@/hooks/useAuth";
import useIntercomTokenManager from "@/hooks/useIntercomTokenManager";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetFullProfileQuery } from "@/store/api/user";
import { getResponsiveBottomPadding } from "@/utils/helpCenter.util";
import runIdentifyWithTimeout from "@/utils/intercomIdentify.util";

type PresentHelpCenterProps = {
  intercomPresenterSource: string;
  isSecured?: boolean;
};

interface UseHelpCenter {
  bottomPadding: number;
  getHelpCenterUnreadMessagesCount: () => Promise<number>;
  hideInAppNotifications: () => Promise<void>;
  identifyHelpCenter: () => Promise<void>;
  loginUnauthenticatedHelpCenter: () => Promise<void>;
  logoutHelpCenter: () => Promise<void>;
  presentHelpCenter: (props: PresentHelpCenterProps) => Promise<void>;
}

/**
 * Hook for managing Intercom Help Center functionality.
 *
 * @param shouldSkipGetFullProfile - Skip fetching user profile during app cold start initialization.
 * When true, prevents `useGetFullProfileQuery` from firing prematurely. This is necessary because
 * useHelpCenter is shown/rendered at the login page/app startup, and the Auth0 SDK may report
 * `isAuthenticated: true` from cached state before the app completes credential validation and
 * cleanup. Firing API calls during this phase can cause errors or use stale credentials.
 */
const useHelpCenter = (
  { shouldSkipGetFullProfile }: { shouldSkipGetFullProfile: boolean } = {
    shouldSkipGetFullProfile: false,
  },
): UseHelpCenter => {
  const { captureException, captureMessage } = useAppMonitor();
  const { clearTokenData, ensureTokenIsSet } = useIntercomTokenManager();
  const { isAuthenticated } = useAuth();
  const { data: fullProfileData } = useGetFullProfileQuery(undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    skip: !isAuthenticated || shouldSkipGetFullProfile,
  });
  const { track } = useAnalytics();
  const { height } = useWindowDimensions();

  // NOTE: pushing launcher visibility/padding to Intercom is owned solely by useIntercomLauncherVisibility (called once in RootNavigator).
  // this guarantees a singleton instance of that hook/logic, rather than a new instance per caller of this useHelpCenter hook
  const bottomPadding = getResponsiveBottomPadding(height);

  const identifyHelpCenter = useCallback(async () => {
    if (!fullProfileData) return;
    const { email, externalId } = fullProfileData;
    if (isEmpty(email) || isEmpty(externalId)) return;
    try {
      await runIdentifyWithTimeout({
        email,
        ensureTokenIsSet,
        externalId,
      });
    } catch (caughtErr) {
      captureException(caughtErr);
      captureMessage(
        "Intercom identify (loginUserWithUserAttributes) failed or timed out",
      );
    }
  }, [captureException, captureMessage, ensureTokenIsSet, fullProfileData]);

  const presentHelpCenter = useCallback(
    async ({
      intercomPresenterSource,
      isSecured = true,
    }: PresentHelpCenterProps) => {
      try {
        if (isSecured) {
          await ensureTokenIsSet();
        }
        track("Equip Service Desk Clicked", {
          [AnalyticsFieldName.Source]: intercomPresenterSource,
        });
        await Intercom.present();
      } catch (error) {
        captureException(error);
        throw error;
      }
    },
    [captureException, ensureTokenIsSet, track],
  );

  const loginUnauthenticatedHelpCenter = useCallback(async () => {
    try {
      await Intercom.loginUnidentifiedUser();
    } catch (error) {
      captureException(error);
      throw error;
    }
  }, [captureException]);

  const hideInAppNotifications = useCallback(async () => {
    try {
      await Intercom.setInAppMessageVisibility(Visibility.GONE);
    } catch (error) {
      captureException(error);
    }
  }, [captureException]);

  const logoutHelpCenter = useCallback(async () => {
    try {
      await hideInAppNotifications();
      await Intercom.logout();
      clearTokenData();
    } catch (error) {
      captureException(error);
    }
  }, [captureException, clearTokenData, hideInAppNotifications]);

  const getHelpCenterUnreadMessagesCount =
    useCallback(async (): Promise<number> => {
      try {
        const count = await Intercom.getUnreadConversationCount();
        return count;
      } catch (error) {
        captureException(error);
        return 0;
      }
    }, [captureException]);

  return {
    bottomPadding,
    getHelpCenterUnreadMessagesCount,
    hideInAppNotifications,
    identifyHelpCenter,
    loginUnauthenticatedHelpCenter,
    logoutHelpCenter,
    presentHelpCenter,
  };
};

export default useHelpCenter;
